import type React from 'react';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  Calculator,
  CheckCircle2,
  Handshake,
  Mail,
  PhoneCall,
  RefreshCw,
  Search,
  Sparkles,
} from 'lucide-react';
import { useComparateurStore } from './useComparateurStore';
import { easeStripe } from '../../../lib/motion';
import { LEGAL } from '@shared/constants/legal-info';

export function StepThankYou() {
  const store = useComparateurStore();
  const d = store.dossier;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3, ease: easeStripe }}
      className="py-2 text-center"
    >
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-brand-50 ring-8 ring-brand-50/60">
        <CheckCircle2 className="h-8 w-8 text-brand-600" strokeWidth={1.5} />
      </div>
      <h3 className="mt-5 h-section">
        Votre consultant prend le relais
      </h3>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-600">
        Un consultant énergie vous recontacte sous{' '}
        <strong className="text-ink">24h ouvrées</strong>{' '}
        {d?.company && (
          <>
            pour <strong className="text-ink">{d.company.name}</strong>.
          </>
        )}
      </p>

      {d?.contact?.email && (
        <div className="mx-auto mt-5 inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50/60 px-3.5 py-1.5 text-[12px] text-brand-900">
          <Mail className="h-3.5 w-3.5" />
          E-mail de confirmation envoyé à <strong>{d.contact.email}</strong>
        </div>
      )}

      <div className="mt-6 grid gap-3 text-left sm:grid-cols-2">
        <NextStepCard
          icon={<Search className="h-4 w-4 text-brand-600" strokeWidth={1.5} />}
          title="Analyse de votre profil"
          text="Nous étudions votre code NAF, vos factures et vos contraintes pour cibler les offres marché comparées."
        />
        <NextStepCard
          icon={<BarChart3 className="h-4 w-4 text-brand-600" strokeWidth={1.5} />}
          title="Comparatif sur-mesure"
          text="Vous recevez un comparatif chiffré (€/an, durée d'engagement, conditions de sortie) avec recommandation argumentée."
        />
        <NextStepCard
          icon={<Handshake className="h-4 w-4 text-brand-600" strokeWidth={1.5} />}
          title="Négociation fournisseur"
          text="Sans partenariat exclusif, nous négocions pour vous les conditions et le prix auprès du fournisseur retenu."
        />
        <NextStepCard
          icon={<RefreshCw className="h-4 w-4 text-brand-600" strokeWidth={1.5} />}
          title="Renégociation à échéance"
          text="Nous vous accompagnons dans la durée : renégociation à l'échéance, alerte hausse marché, suivi de votre contrat."
        />
      </div>

      {/* Cross-sell : primes CEE adaptées au NAF de l'entreprise */}
      {d?.company?.nafCode && <CeeCrossSell nafCode={d.company.nafCode} />}

      <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        {LEGAL.phone && (
          <a
            href={`tel:${LEGAL.phone.replace(/\s/g, '')}`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-ink px-4 py-2 text-[13px] font-semibold text-ivory-50 transition-colors hover:bg-stone-800"
          >
            <PhoneCall className="h-3.5 w-3.5" />
            {LEGAL.phone}
          </a>
        )}
        <a
          href="/cas-clients"
          className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand-700 underline-offset-2 hover:underline"
        >
          Voir des cas clients
          <ArrowRight className="h-3.5 w-3.5" />
        </a>
      </div>

      <p className="mt-6 inline-flex items-center gap-1.5 text-[11px] text-stone-400">
        <Sparkles className="h-3 w-3" />
        Comparateur indépendant des fournisseurs d&apos;énergie. Aucun partenariat exclusif.
      </p>
    </motion.div>
  );
}

/* ─── Cross-sell CEE basé sur le code NAF ──────────────────── */

interface CeeRecommendedFiche {
  code: string;
  label: string;
  sector: string;
  estimationType: string;
  calculable: boolean;
  url: string;
}

/** Travaux génériques pour sonder les fiches du secteur déduit du NAF */
const GENERIC_TRAVAUX = 'isolation_toiture,pompe_chaleur,eclairage_led,ventilation';

function CeeCrossSell({ nafCode }: { nafCode: string }) {
  const [fiches, setFiches] = useState<CeeRecommendedFiche[]>([]);

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ travaux: GENERIC_TRAVAUX, naf: nafCode });
    fetch(`/api/cee/recommend?${params.toString()}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json: { fiches?: CeeRecommendedFiche[] } | null) => {
        if (!cancelled && json?.fiches) setFiches(json.fiches.slice(0, 3));
      })
      .catch(() => {
        /* cross-sell silencieux */
      });
    return () => {
      cancelled = true;
    };
  }, [nafCode]);

  if (fiches.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: easeStripe }}
      className="mt-6 rounded-2xl border border-accent-200/70 bg-gradient-to-br from-accent-50/50 via-white to-brand-50/30 p-4 text-left"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-accent-700" strokeWidth={2.2} />
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-accent-900">
          En attendant — vos primes CEE potentielles
        </p>
      </div>
      <p className="mt-1 text-[12px] leading-relaxed text-stone-600">
        D&apos;après votre secteur d&apos;activité, ces opérations d&apos;efficacité
        énergétique ouvrent droit à des primes — cumulables avec votre nouveau contrat.
      </p>
      <div className="mt-3 space-y-1.5">
        {fiches.map((f) => (
          <a
            key={f.code}
            href={f.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 rounded-lg bg-white/80 px-3 py-2 ring-1 ring-inset ring-accent-200/50 transition-colors hover:ring-accent-300"
          >
            <span className="shrink-0 font-mono text-[10px] font-bold tracking-wider text-stone-500">
              {f.code}
            </span>
            <span className="min-w-0 flex-1 truncate text-[12px] text-stone-700">{f.label}</span>
            <span className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-accent-800">
              {f.calculable && <Calculator className="h-3 w-3" strokeWidth={2} />}
              {f.calculable ? 'Simuler' : 'Voir'}
              <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </span>
          </a>
        ))}
      </div>
    </motion.div>
  );
}

function NextStepCard(props: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-stone-50/50 p-4">
      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50">{props.icon}</div>
      <p className="mt-2 text-[13px] font-semibold text-ink">{props.title}</p>
      <p className="mt-1 text-[12px] leading-relaxed text-stone-500">{props.text}</p>
    </div>
  );
}
